// ============================================================================
// UTILITY FUNCTIONS AND VALIDATION LOGIC FOR PLACE ORDER MODAL
// ============================================================================

import { v4 as uuidv4 } from "uuid";
import { ProductData, OrderPayload, ValidationError, OrderDetailData } from "./types";

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const createDefaultProductData = (): ProductData => ({
  id: '',
  type: 'door',
  label: '',
  location: '',
  evaOrder: '',
  notes: '',
  netType: '',
  netCount: 0,
  frame: [],
  panel: [],
  panelWindow: [],
  net: [],
  openableType: [],
});

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

export const validateOrderPayload = (payload: OrderPayload): ValidationError[] => {
  const errors: ValidationError[] = [];

  console.log("=== VALIDATING ORDER PAYLOAD ===");
  console.log("Payload:", payload);
  console.log("CustomerId:", payload.customerId);
  console.log("SiteId:", payload.siteId);

  if (!payload.customerId) {
    console.error("Customer ID is missing");
    errors.push({ field: 'customerId', message: 'Customer ID is required' });
  } else {
    // Validate UUID format for customerId
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(payload.customerId)) {
      console.error("Invalid customerId UUID format:", payload.customerId);
      errors.push({ field: 'customerId', message: 'Invalid customer ID format' });
    }
  }

  // siteId is optional - user can create order without selecting a site
  // if (!payload.siteId) {
  //   errors.push({ field: 'siteId', message: 'Site ID is required' });
  // }

  if (!payload.products || payload.products.length === 0) {
    errors.push({ field: 'products', message: 'At least one product is required' });
  } else {
    // Validate each product has required fields
    payload.products.forEach((product, index) => {
      if (!product.name || product.name.trim() === '') {
        errors.push({ field: `products[${index}].name`, message: 'Product name is required' });
      }
      if (!product.type || product.type.trim() === '') {
        errors.push({ field: `products[${index}].type`, message: 'Product type is required' });
      }
    });
  }

  // if (payload.totalAmount <= 0) {
  //   console.log("Total amount is less than or equal to 0:", payload.totalAmount);
  //   console.log("Total amount is less than or NOtes:", payload.notes);
  //   errors.push({ field: 'totalAmount', message: 'Total amount must be greater than 0' });
  // }

  if (!payload.dueDate) {
    errors.push({ field: 'dueDate', message: 'Due date is required' });
  }

  return errors;
};

// ============================================================================
// DATA TRANSFORMATION UTILITIES
// ============================================================================

export const createDefaultOrderDetailData = (siteData: any): OrderDetailData => {
  console.log("=== CREATING DEFAULT ORDER DETAIL DATA ===");
  console.log("siteData:", siteData);
  console.log("siteData?.id:", siteData?.id);
  console.log("siteData?.customerId:", siteData?.customerId);
  
  // UUID validation function
  const isValidUUID = (uuid: string) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };
  
  // Get customerId and siteId from siteData - handle both direct and nested structures
  const customerId = siteData?.customerId || (siteData as any)?.customer?.customerId;
  const siteId = siteData?.id;
  
  console.log("customerId from siteData:", customerId);
  console.log("siteId from siteData:", siteId);
  
  // Validate that we have the required data
  if (!customerId || !siteId || customerId === "undefined" || siteId === "undefined") {
    console.error("Missing required data from siteData");
    console.error("customerId:", customerId);
    console.error("siteId:", siteId);
    throw new Error("Missing customer or site data");
  }
  
  // Validate UUIDs
  if (!isValidUUID(customerId)) {
    console.error("Invalid customerId UUID:", customerId);
    throw new Error("Invalid customer ID format");
  }
  
  if (!isValidUUID(siteId)) {
    console.error("Invalid siteId UUID:", siteId);
    throw new Error("Invalid site ID format");
  }
  
  console.log("Final customerId:", customerId);
  console.log("Final siteId:", siteId);
  
  return {
    id: "",
    customerId: customerId,
    siteId: siteId,
    totalAmount: 0,
    dueDate: new Date().toISOString().split("T")[0],
    notes: "",
    priority: "medium",
    paymentStatus: "pending",
    progressPercentage: 0,
    totalItems: 0,
    completedItems: 0,
    products: ["door" as const],
  };
};

// ============================================================================
// TRANSFORM EXISTING PRODUCTS UTILITY
// ============================================================================

export const transformExistingProducts = (existingProducts: any[]) => {
  console.log("=== TRANSFORMING EXISTING PRODUCTS ===");
  console.log("Existing products:", existingProducts);
  
  // Transform existing products to match current format
  const transformedProducts = existingProducts.map((product: any) => {
    console.log("Transforming product:", product);
    
    // Transform items to the expected format (flat structure)
    const transformItems = (items: any[], itemType: string) => {
      console.log(`=== TRANSFORMING ${itemType.toUpperCase()} ITEMS ===`);
      console.log("Input items:", items);
      if (!items || !Array.isArray(items)) {
        console.log("No items or not an array, returning empty array");
        return [];
      }
      const transformed = items.map((item: any) => {
        console.log(`Transforming ${itemType} item:`, item);
        const transformedItem = {
          id: item.id || uuidv4(),
          type: item.type || itemType,
          quantity: item.quantity || 1,
          width: item.specifications?.width || item.width || "",
          height: item.specifications?.height || item.height || "",
          number: item.specifications?.number || item.number || "",
          material: item.specifications?.material || item.material || "",
          color: item.specifications?.color || item.color || "",
          finish: item.specifications?.finish || item.finish || "",
          member_required: item.specifications?.member_required ?? item.member_required ?? false,
          bar_required: item.specifications?.bar_required ?? item.bar_required ?? false,
          glass_required: item.specifications?.glass_required ?? item.glass_required ?? false, // Note: backend uses glass_required, we use glass_required
          assigned_to: item.assigned_to || "",
          estimated_hours: item.estimated_hours || 0
        };
        console.log(`Transformed ${itemType} item:`, transformedItem);
        return transformedItem;
      });
      console.log(`Final transformed ${itemType} items:`, transformed);
      return transformed;
    };

    // Handle different backend response structures
    let frameItems = [];
    let panelItems = [];
    let panelWindowItems = [];
    let netItems = [];
    let openableTypeItems = [];

    if (product.itemTypes) {
      // Backend returns itemTypes structure
      console.log("=== EXTRACTING FROM ITEMTYPES ===");
      console.log("product.itemTypes:", product.itemTypes);
      frameItems = product.itemTypes.frame || [];
      panelItems = product.itemTypes.panel || [];
      panelWindowItems = product.itemTypes.panelWindow || [];
      netItems = product.itemTypes.net || [];
      openableTypeItems = product.itemTypes.openableType || [];
      console.log("frameItems:", frameItems);
      console.log("panelItems:", panelItems);
      console.log("panelWindowItems:", panelWindowItems);
    } else if (product.items && Array.isArray(product.items)) {
      // Backend returns items in a single array - categorize by type
      console.log("=== EXTRACTING FROM ITEMS ARRAY ===");
      console.log("product.items:", product.items);
      
      product.items.forEach((item: any) => {
        const itemType = item.type?.toLowerCase();
        console.log(`Categorizing item type: ${itemType}`);
        
        switch (itemType) {
          case 'frame':
            frameItems.push(item);
            break;
          case 'panel':
            panelItems.push(item);
            break;
          case 'panelwindow':
          case 'panel window':
            panelWindowItems.push(item);
            break;
          case 'net':
          case 'net fixing':
            netItems.push(item);
            break;
          case 'openabletype':
          case 'openable type':
            openableTypeItems.push(item);
            break;
          default:
            console.log(`Unknown item type: ${itemType}, skipping`);
        }
      });
      
      console.log("frameItems:", frameItems);
      console.log("panelItems:", panelItems);
      console.log("panelWindowItems:", panelWindowItems);
    } else {
      // Direct arrays (already in flat structure)
      console.log("=== EXTRACTING FROM DIRECT ARRAYS ===");
      frameItems = product.frame || [];
      panelItems = product.panel || [];
      panelWindowItems = product.panelWindow || [];
      netItems = product.net || [];
      openableTypeItems = product.openableType || [];
      console.log("frameItems:", frameItems);
      console.log("panelItems:", panelItems);
      console.log("panelWindowItems:", panelWindowItems);
    }

    const transformedProduct = {
      id: product.id,
      type: product.type || "door",
      label: product.name || product.label || "",
      location: product.location || "",
      evaOrder: product.eva_order || product.evaOrder || "",
      notes: product.notes || "",
      netType: product.netType || "",
      netCount: product.net_quantity || product.netCount || 0,
      // Direct arrays instead of itemTypes wrapper
      frame: transformItems(frameItems, "frame"),
      panel: transformItems(panelItems, "panel"),
      panelWindow: transformItems(panelWindowItems, "panelWindow"),
      net: transformItems(netItems, "net"),
      openableType: transformItems(openableTypeItems, "openableType")
    };
    
    console.log("=== FINAL TRANSFORMED PRODUCT ===");
    console.log("Transformed product:", transformedProduct);
    console.log("Frame items count:", transformedProduct.frame.length);
    console.log("Panel items count:", transformedProduct.panel.length);
    console.log("PanelWindow items count:", transformedProduct.panelWindow.length);
    console.log("Net items count:", transformedProduct.net.length);
    console.log("OpenableType items count:", transformedProduct.openableType.length);
    return transformedProduct;
  });
  
  console.log("Final transformed products:", transformedProducts);
  return transformedProducts;
};
